/**
 * useMatchingConfig — global matching_config (scope='global') CRUD.
 *
 * Obsluga jednego rekordu globalnego — per-map overrides mozna dodac pozniej.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MatchingConfigRow {
  id: string;
  scope: "global" | "map";
  map_id: string | null;
  containment_auto_threshold: number;
  containment_borderline_threshold: number;
  require_entity_match: boolean;
  require_frame_match_for_auto: boolean;
  send_weak_to_llm: boolean;
  llm_batch_size: number;
  llm_concurrency: number;
  borderline_quality_as: "matched_keep" | "matched_refurbish" | "partial";
  created_at: string;
  updated_at: string;
}

const QUERY_KEY = ["matching-config", "global"] as const;

async function fetchGlobalConfig(): Promise<MatchingConfigRow | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("matching_config")
    .select("*")
    .eq("scope", "global")
    .maybeSingle();

  if (error) throw error;
  return (data as MatchingConfigRow | null) ?? null;
}

type UpdatePatch = Partial<
  Pick<
    MatchingConfigRow,
    | "containment_auto_threshold"
    | "containment_borderline_threshold"
    | "require_entity_match"
    | "require_frame_match_for_auto"
    | "send_weak_to_llm"
    | "llm_batch_size"
    | "llm_concurrency"
    | "borderline_quality_as"
  >
>;

async function updateGlobalConfig(patch: UpdatePatch): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("matching_config")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("scope", "global");

  if (error) throw error;
}

export function useMatchingConfig() {
  const qc = useQueryClient();

  const config = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchGlobalConfig,
  });

  const update = useMutation({
    mutationFn: (patch: UpdatePatch) => updateGlobalConfig(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return { config, update };
}
